export interface StoragePersistenceStatus {
  /** navigator.storage.persist() has been requested and granted. */
  persisted: boolean;
  /** The Storage Manager / Persistence API is available at all. */
  supported: boolean;
}

/**
 * Request durable (persistent) storage from the browser so the OS is less
 * likely to evict ZenTrack data under storage pressure. Never throws — a denial
 * or unsupported environment simply returns the current status.
 */
export async function requestPersistentStorage(): Promise<StoragePersistenceStatus> {
  if (typeof navigator === "undefined" || !navigator.storage?.persist) {
    return { persisted: false, supported: false };
  }

  try {
    let persisted = await navigator.storage.persisted();

    if (!persisted) {
      // Best-effort: some browsers resolve persist() to false without a prompt.
      persisted = await navigator.storage.persist();
    }

    return { persisted, supported: true };
  } catch {
    return { persisted: false, supported: true };
  }
}

/** Read the current persistence status without prompting. */
export async function readStoragePersistence(): Promise<StoragePersistenceStatus> {
  if (typeof navigator === "undefined" || !navigator.storage?.persisted) {
    return { persisted: false, supported: false };
  }

  try {
    const persisted = await navigator.storage.persisted();
    return { persisted, supported: true };
  } catch {
    return { persisted: false, supported: true };
  }
}
